// #region imports
import {
  Sheet,
  CellPosition,
  Freeze,
  Viewport,
  FilterResults,
  IRange,
  EntitySize
} from '../../types';
import { getDefaultArea } from '../area/area';
import { inRange } from '../../../core/utils/general/general';
import { getEntitySize } from '../viewport/viewportUtils';
import { isEqual } from 'lodash';
import { DataDirection } from '../../../core/types/ui/position';
import { getUnHiddenPosition, getViewportStartEnd } from './virtualViewport';
// #endregion

// #region types
export interface Boundaries {
  minY: number;
  maxY: number;
  minX: number;
  maxX: number;
}

interface CalculateIndexPositionParams {
  // entries: Col[] | Row[];
  entitySizes: EntitySize[];
  hiddenRanges: IRange[];
  clientPos: number;
  minPos: number;
  maxPos: number;
  viewportIndexes: number[];
  maxIndex: number;
  nonFrozenIndex: number;
  defaultItemSize: number;
  isRtl: boolean;
  zoom: number;
  freeze: Freeze;
  filterResults?: FilterResults;
  largerThanCenter?: boolean;
}

type GetFreezeAdjustedIndex = Omit<CalculateIndexPositionParams, 'maxIndex'> & {
  areaEnd: number;
  index: number;
  frozenIndexes: number[];
};
export type SheetParamsForMove = Pick<
  Sheet,
  | 'colSizes'
  | 'rowSizes'
  | 'freezedCols'
  | 'freezedRows'
  | 'maxColIndex'
  | 'maxRowIndex'
  | 'defaultColWidth'
  | 'defaultRowHeight'
  | 'zoom'
  | 'hiddenCols'
  | 'hiddenRows'
>;
// #endregion

const calculateSkipIndexes = (
  boundaryMin: number,
  clientPos: number,
  threshold = 5
) => {
  const positionDifference = Math.abs(boundaryMin - clientPos);
  const dividedValue = Math.floor(positionDifference / threshold);
  return Math.max(dividedValue, 1);
};
// Math.max(Math.floor(Math.abs(boundaryMin - clientPos) / threshold), 1);

/*
  largerThanCenter variable will be used for frozen lines movement in 
  which we don't need to scroll the viewport and the index will jump only if the mouse pos is greater than it's center 
  and in case of largerThanCenter variable if the mouse position less than the minimum then we will we will use the index minus one which means that
  we have to freeze to Rohit and Max will work fine so larger than Center will be used for this
*/

// #region get cell position from mouse position
export const getCellPositionFromMousePosition = (
  sheet: SheetParamsForMove,
  clientX: number,
  clientY: number,
  freezeBoundaries: Boundaries,
  boundaries: Boundaries,
  colViewport: Viewport,
  rowViewport: Viewport,
  isRtl: boolean,
  unFreezedCi: number | undefined,
  unFreezedRi: number | undefined,
  area = getDefaultArea(),
  filterResults?: FilterResults,
  largerThanCenter?: boolean
): CellPosition => ({
  ci: getColPositionFromMousePosition(
    sheet,
    clientX,
    freezeBoundaries.minX,
    freezeBoundaries.maxX,
    boundaries.minX,
    boundaries.maxX,
    colViewport,
    isRtl,
    unFreezedCi,
    area,
    largerThanCenter
  ),
  ri: getRowPositionFromMousePosition(
    sheet,
    clientY,
    freezeBoundaries.minY,
    freezeBoundaries.maxY,
    boundaries.minY,
    boundaries.maxY,
    rowViewport,
    unFreezedRi,
    area,
    largerThanCenter,
    filterResults
  ),
});
// #endregion

// #region get col position
export const getColPositionFromMousePosition = (
  sheet: SheetParamsForMove,
  clientX: number,
  freezeMinX: number,
  freezeMaxX: number,
  paneMinX: number,
  paneMaxX: number,
  viewport: Viewport,
  isRtl: boolean,
  unFreezedCi: number | undefined,
  area = getDefaultArea(),
  largerThanCenter?: boolean
) => {
  const { freezedCols, maxColIndex, zoom } = sheet;

  const params: CalculateIndexPositionParams = {
    // entries: sheet.cols,
    entitySizes: sheet.colSizes,
    hiddenRanges: sheet.hiddenCols,
    clientPos: clientX,
    minPos: paneMinX,
    maxPos: paneMaxX,
    viewportIndexes: viewport.nonFrozenIndexes,
    maxIndex: maxColIndex,
    nonFrozenIndex: unFreezedCi ?? -1,
    defaultItemSize: sheet.defaultColWidth,
    isRtl,
    zoom,
    freeze: freezedCols,
    largerThanCenter,
  };

  let ci = calculateIndexPosition(params);

  ci = getFreezeAdjustedIndex({
    ...params,
    areaEnd: area.end.ci,
    index: ci,
    frozenIndexes: viewport.frozenIndexes,
    minPos: freezeMinX,
    maxPos: freezeMaxX,
  });

  if (
    largerThanCenter &&
    ci == -1 &&
    freezedCols.frozen &&
    (isRtl ? clientX < freezeMinX : clientX > freezeMaxX)
  ) {
    const { end } = getViewportStartEnd(viewport.frozenIndexes);
    ci = end;
  }

  return ci;
};
// #endregion

// #region get row position
export const getRowPositionFromMousePosition = (
  sheet: SheetParamsForMove,
  clientY: number,
  freezeMinY: number,
  freezeMaxY: number,
  paneMinY: number,
  paneMaxY: number,
  viewport: Viewport,
  unFreezedRi: number | undefined,
  area = getDefaultArea(),
  largerThanCenter?: boolean,
  filterResults?: FilterResults
) => {
  const { freezedRows, maxRowIndex, zoom } = sheet;
  const params: CalculateIndexPositionParams = {
    // entries: sheet.rows,
    entitySizes: sheet.rowSizes,
    hiddenRanges: sheet.hiddenRows,
    clientPos: clientY,
    minPos: paneMinY,
    maxPos: paneMaxY,
    viewportIndexes: viewport.nonFrozenIndexes,
    maxIndex: maxRowIndex,
    nonFrozenIndex: unFreezedRi ?? -1,
    defaultItemSize: sheet.defaultRowHeight,
    isRtl: false,
    zoom,
    freeze: freezedRows,
    filterResults,
    largerThanCenter,
  };

  let ri = calculateIndexPosition(params);

  ri = getFreezeAdjustedIndex({
    ...params,
    frozenIndexes: viewport.frozenIndexes,
    index: ri,
    areaEnd: area.end.ri,
    minPos: freezeMinY,
    maxPos: freezeMaxY,
  });

  if (
    largerThanCenter &&
    ri == -1 &&
    freezedRows.frozen &&
    clientY > freezeMaxY
  ) {
    const { end } = getViewportStartEnd(viewport.frozenIndexes);
    ri = end;
  }

  return ri;
};
// #endregion

// #region get freeze adjusted index
export const getFreezeAdjustedIndex = (params: GetFreezeAdjustedIndex) => {
  const {
    isRtl,
    index,
    viewportIndexes,
    minPos,
    maxPos,
    freeze,
    frozenIndexes,
    clientPos,
    areaEnd,
    nonFrozenIndex,
    largerThanCenter,
  } = params;
  let updatedIndex = index;

  const { start: viewportStart } = getViewportStartEnd(viewportIndexes);

  const { frozen } = freeze;
  const { start, end } = getViewportStartEnd(frozenIndexes);

  if (isEqual(minPos, maxPos)) return index;

  if (frozen && inRange(clientPos, minPos, maxPos)) {
    const indexPosParams: CalculateIndexPositionParams = {
      ...params,
      viewportIndexes: params.frozenIndexes,
      maxIndex: end,
    };
    const freezedIndex = calculateIndexPosition(indexPosParams);

    if (areaEnd > nonFrozenIndex && viewportStart > nonFrozenIndex) {
    } else {
      updatedIndex = freezedIndex;
    }
  } else if (
    !largerThanCenter &&
    frozen &&
    (isRtl ? clientPos >= maxPos : clientPos <= minPos) && // clientPos <= minPos
    viewportStart === nonFrozenIndex
  ) {
    updatedIndex = start;
  } else if (!largerThanCenter && areaEnd <= end) {
    updatedIndex = nonFrozenIndex;
  }
  return updatedIndex;
};
// #endregion

// #region calculate index position both rtl | non rtl
const calculateIndexPosition = (params: CalculateIndexPositionParams) => {
  if (params.isRtl) {
    return calculateIndexForRtl(params);
  } else {
    return calculateIndexNonRtl(params);
  }
};

const calculateIndexForRtl = (params: CalculateIndexPositionParams) => {
  const { clientPos, minPos, maxPos, largerThanCenter, nonFrozenIndex } =
    params;
  const { start: vs, end: ve } = getViewportStartEnd(params.viewportIndexes);
  let index = vs;

  if (clientPos >= maxPos) {
    const skipItem = calculateSkipIndexes(clientPos, maxPos);
    index = largerThanCenter
      ? -1
      : getUnHiddenPosition(
          params.entitySizes,
          params.hiddenRanges,
          vs,
          params.maxIndex,
          DataDirection.Backward,
          params.freeze,
          params.filterResults,
          skipItem
        );
    if (nonFrozenIndex !== -1 && !largerThanCenter)
      index = Math.max(nonFrozenIndex, index);
    // : Math.max(viewportStart - skipItem, minIndex);
  } else if (clientPos <= minPos) {
    const skipItem = calculateSkipIndexes(minPos, clientPos);
    index = largerThanCenter
      ? ve
      : getUnHiddenPosition(
          params.entitySizes,
          params.hiddenRanges,
          ve,
          params.maxIndex,
          DataDirection.Forward,
          params.freeze,
          params.filterResults,
          skipItem
        );
    // : Math.min(viewportEnd + skipItem, maxIndex);
  } else {
    index = getIndexFromMousePositionRtl(
      params.entitySizes,
      maxPos,
      clientPos,
      params.defaultItemSize,
      params.viewportIndexes,
      params.zoom,
      largerThanCenter
    );
  }

  return index;
};

const calculateIndexNonRtl = (params: CalculateIndexPositionParams) => {
  const { clientPos, minPos, maxPos, largerThanCenter, nonFrozenIndex } =
    params;
  const { start: vs, end: ve } = getViewportStartEnd(params.viewportIndexes);
  let index = vs;

  if (clientPos <= minPos) {
    const skipItem = calculateSkipIndexes(minPos, clientPos);
    index = largerThanCenter
      ? -1
      : getUnHiddenPosition(
          params.entitySizes,
          params.hiddenRanges,
          vs,
          params.maxIndex,
          DataDirection.Backward,
          params.freeze,
          params.filterResults,
          skipItem
        );
    if (nonFrozenIndex !== -1 && !largerThanCenter)
      index = Math.max(nonFrozenIndex, index);
  } else if (clientPos >= maxPos) {
    const skipItem = calculateSkipIndexes(clientPos, maxPos);
    index = largerThanCenter
      ? ve
      : getUnHiddenPosition(
          params.entitySizes,
          params.hiddenRanges,
          ve,
          params.maxIndex,
          DataDirection.Forward,
          params.freeze,
          params.filterResults,
          skipItem
        );
  } else {
    index = getIndexFromMousePosition(
      params.entitySizes,
      minPos,
      clientPos,
      params.defaultItemSize,
      params.viewportIndexes,
      params.zoom,
      largerThanCenter
    );
  }

  return index;
};
// #endregion

// #region pick viewport index based on mouse position rtl | non rtl
const getIndexFromMousePositionRtl = (
  // entries: T[],
  entitySizes: EntitySize[],
  endPosition: number,
  mousePosition: number,
  defaultSize: number,
  viewportIndexes: number[],
  zoom: number,
  largerThanCenter?: boolean
): number => {
  const vs = viewportIndexes[0] ?? 0;
  let index = largerThanCenter ? -1 : vs;

  let sum = endPosition;

  for (let pos of viewportIndexes) {
    // const item = entries[pos];
    // const size = getRowColSize(defaultSize, item, zoom);
    const size = getEntitySize(entitySizes, pos, defaultSize, zoom);

    let placement = largerThanCenter ? sum - Math.floor(size / 2) : sum;

    if (mousePosition > placement) {
      break;
    } else if (inRange(mousePosition, sum - size, placement)) {
      index = pos;
      break;
    }
    sum -= size;
    index = pos;
  }

  return index;
};

export const getIndexFromMousePosition = (
  entitySizes: EntitySize[],
  startPosition: number,
  mousePosition: number,
  defaultSize: number,
  indexes: number[],
  zoom: number,
  largerThanCenter?: boolean
): number => {
  const vs = indexes[0] ?? 0;
  let index = largerThanCenter ? -1 : vs;

  let sum = startPosition;

  for (let pos of indexes) {
    // const item = entries[pos];
    // const size = getRowColSize(defaultSize, item, zoom);
    const size = getEntitySize(entitySizes, pos, defaultSize, zoom);

    let placement = largerThanCenter ? sum + Math.floor(size / 2) : sum;

    if (mousePosition < placement) {
      break;
    } else if (inRange(mousePosition, placement, sum + size)) {
      index = pos;
      break;
    }
    sum += size;
    index = pos;
  }
  return index;
};
// #endregion
